import {
  Links,
  NavLink,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useLocation,
  useNavigate,
} from "react-router";
import type { Route } from "./+types/root";

import appStylesHref from "./css/app.css?url";
import bootstrapGridHref from "./css/bootstrap-grid.min.css?url";
import faviconUrl from "/favicon.png?url";
import './assets/i18n';
import i18n from './assets/i18n';
import { useEffect } from "react";
import 'react-tooltip/dist/react-tooltip.css'
import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from "aws-amplify/auth";
import ErrorPage from "./components/ErrorPage";
import { useTranslation } from 'react-i18next';
import { GlobalWebSocket } from './components/GlobalWebSocket';
import useUtmLinkUpdater from "./lib/utmLinkUpdater";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID,
      identityPoolId: "",
      loginWith: {
        email: true,
        oauth: {
          domain: import.meta.env.VITE_COGNITO_OAUTH_DOMAIN, // Ensure this environment variable is set
          scopes: ["openid", "profile", "email", "aws.cognito.signin.user.admin"],
          redirectSignIn: import.meta.env.VITE_REDIRECT_SIGN_IN.split(','), // Ensure this environment variable is set
          redirectSignOut: import.meta.env.VITE_REDIRECT_SIGN_OUT.split(','), // Ensure this environment variable is set
          responseType: 'code' // For Authorization code grant
        }
      },
      signUpVerificationMethod: "code",
      //   userAttributes: {
      //     email: {
      //       required: true,
      //     },
      //   },
      allowGuestAccess: true,
      //   passwordFormat: {
      //     minLength: 8,
      //     requireLowercase: true,
      //     requireUppercase: true,
      //     requireNumbers: true,
      //     requireSpecialCharacters: true,
      //   },
    },
  },
});

export const links: Route.Links = () => [
  { rel: "stylesheet", href: bootstrapGridHref },
  { rel: "stylesheet", href: appStylesHref },
  { rel: "icon", type: "image/png", href: faviconUrl }
];

export default function App() {
  useUtmLinkUpdater();

  useEffect(() => {
    // Imposta il tema dark di default
    document.documentElement.classList.add('dark');
  }, []);

  return (
    <>
      <GlobalWebSocket />
      <Outlet />
    </>
  );
}

// existing imports & exports

export function HydrateFallback() {
  const { t } = useTranslation();

  return (
    <div id="loading-splash">
      <div id="loading-splash-spinner" />
      <p>{t("loading_please_wait")}</p>
    </div>
  );
}

// The Layout component is a special export for the root route.
// It acts as your document's "app shell" for all route components, HydrateFallback, and ErrorBoundary
// For more information, see https://reactrouter.com/explanation/special-files#layout-export
export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" className="dark">
      <head>
        <title>{i18n.t("app_title")}</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
        <Links />
        {/* Google Tag Manager */}
        <script dangerouslySetInnerHTML={{
          __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-5VKVDBQT');`
        }} />
        {/* Google tag (gtag.js) */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-2T78KT83FV"></script>
        <script dangerouslySetInnerHTML={{
          __html: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-2T78KT83FV');
        `
        }} />
        {/* End Google Tag Manager */}
        <script src={`https://www.google.com/recaptcha/api.js?render=${import.meta.env.VITE_CAPTCHA_SITEKEY}`} async defer></script>
        {/* Hotjar Tracking Code for https://www.kodesherpa.tech/ */}
        <script dangerouslySetInnerHTML={{
          __html: `
          (function(h,o,t,j,a,r){
              h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
              h._hjSettings={hjid:5356285,hjsv:6};
              a=o.getElementsByTagName('head')[0];
              r=o.createElement('script');r.async=1;
              r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
              a.appendChild(r);
          })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
        `
        }} />

        {/* Meta Pixel Code */}
        <script dangerouslySetInnerHTML={{
          __html: `
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod ?
            n.callMethod.apply(n, arguments) : n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '1413582287156866');
          fbq('track', 'PageView');
        `
        }} />
        {/* End Meta Pixel Code */}

        {/* Reddit Pixel */}
        <script dangerouslySetInnerHTML={{
          __html: `
          !function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);rdt('init','a2_heqvs16votct');rdt('track', 'PageVisit');
          `
        }} />
        {/* End Reddit Pixel */}

      </head>
      <body id="root" className="bg-[var(--theme-background)] text-gray-100">
        {/* Google Tag Manager (noscript) */}
        <noscript><iframe src="https://www.googletagmanager.com/ns.html?id=GTM-5VKVDBQT"
          height="0" width="0" style={{ display: 'none', visibility: 'hidden' }}></iframe></noscript>
        {/* End Google Tag Manager (noscript) */}
        {/* Meta Pixel Code (noscript) */}
        <noscript dangerouslySetInnerHTML={{
          __html: `<img height="1" width="1" style="display:none"
            src="https://www.facebook.com/tr?id=1413582287156866&ev=PageView&noscript=1"
          />`
        }} />
        {/* End Meta Pixel Code (noscript) */}
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

// The top most error boundary for the app, rendered when your app throws an error
// For more information, see https://reactrouter.com/start/framework/route-module#errorboundary
export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  let message = t("oops");
  let details = t("unexpected_error");
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : t("error");
    details =
      error.status === 404
        ? t("page_not_found")
        : error.statusText || details;

    // Handle 404 for unknown routes (like /dashboard)
    if (error.status === 404) {
      // Redirect unknown routes to home if user is authenticated, otherwise landing page
      const siteRoot = (import.meta.env.VITE_SITE_ROOT || "/chat").replace(/\/$/, "");
      const currentPath = location.pathname;

      // If path contains "dashboard" or other unknown routes, redirect to home
      if (currentPath.includes("dashboard") || currentPath.includes("//")) {
        setTimeout(() => {
          navigate(siteRoot);
        }, 2000);
      }
    }
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return <ErrorPage message={message} details={details} stack={stack} />;
}
